import React, { useState } from 'react';
import {
  Alert, Button, Col, Descriptions, Drawer, Row, Skeleton, Space, Spin, Statistic, Tooltip, Typography,
} from 'antd';
import { BarChartOutlined, ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CampanaBrevo, CampanaTipoSemana, CampanasSemana } from '../../api/cxPanelApi';
import { fct } from '../../fct/tokens/tokens';
import { FctEstado } from '../../fct/react';
import type { FctFamilia, FctNombreIcono } from '../../fct/react';
import { TIPO_COLOR_HEX, urlCampanaBrevo } from './panelCx';

const { Text } = Typography;

/** Estado de Brevo en castellano, con su familia del semáforo del manual (D-GCVSC-023). El borrador
 *  va en aviso: está esperando a que María lo revise y lo envíe desde Brevo (D-CX-62). */
const ESTADO: Record<string, { texto: string; familia: FctFamilia; icono?: FctNombreIcono }> = {
  draft: { texto: 'Borrador · por enviar', familia: 'aviso', icono: 'editar' },
  queued: { texto: 'En cola', familia: 'proceso' },
  in_process: { texto: 'Enviándose', familia: 'proceso' },
  sent: { texto: 'Enviada', familia: 'exito' },
  suspended: { texto: 'Suspendida', familia: 'riesgo' },
  archive: { texto: 'Archivada', familia: 'neutro', icono: 'carpeta' },
};

const estadoDe = (b: CampanaBrevo) => (b.estado ? ESTADO[b.estado] ?? { texto: b.estado, familia: 'neutro' as FctFamilia } : null);

const fechaHora = (iso?: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY HH:mm') : '—');

/** «12 (85 %)»: valor y su porcentaje sobre la base; sin base, solo el valor. */
const conPorcentaje = (valor?: number | null, base?: number | null): string => {
  if (valor == null) return '—';
  if (!base) return String(valor);
  return `${valor} (${Math.round((valor / base) * 100)} %)`;
};

/** Por qué Brevo cuenta menos que Gescom: no es un error, es cómo funciona Brevo. */
const NOTA_DIFERENCIA = 'Brevo no cuenta a los contactos sin email, dados de baja o bloqueados, así que suele tener algunos menos que Gescom.';

/** Enlace a la campaña en Brevo (allí está el detalle por contacto: quién abrió, clicó o rebotó). */
const AbrirEnBrevo: React.FC<{ id: number; size?: 'small' | 'middle' }> = ({ id, size = 'small' }) => (
  <Button size={size} icon={<ExportOutlined />} href={urlCampanaBrevo(id)} target="_blank" rel="noopener noreferrer">
    Abrir en Brevo
  </Button>
);

interface Props {
  datos: CampanasSemana | null;
  loading: boolean;
  /** Cuántos envíos calcula Gescom por tipo para esa semana. */
  calculados: Record<string, number>;
}

/** Una tarjeta por tipo: nombre de la campaña, estado en Brevo, métricas y enlace. */
const TarjetaCampana: React.FC<{
  c: CampanaTipoSemana;
  calculados: number;
  consultado: boolean;
  onMetricas: (c: CampanaTipoSemana) => void;
}> = ({ c, calculados, consultado, onMetricas }) => {
  const b = c.brevo;
  const estado = b ? estadoDe(b) : null;

  return (
    <div style={{
      border: `1px solid ${fct.linea.tarjeta}`, borderLeft: `4px solid ${TIPO_COLOR_HEX[c.tipo] ?? fct.linea.control}`,
      borderRadius: 6, padding: '8px 12px', height: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text strong ellipsis={{ tooltip: b?.nombre ?? c.nombre }} style={{ minWidth: 0 }}>
          {b?.nombre ?? c.nombre}
        </Text>
        {estado && <FctEstado personalizado={{ etiqueta: estado.texto, familia: estado.familia, icono: estado.icono }} />}
        {b && (
          <Space size={4} style={{ marginLeft: 'auto', flexShrink: 0 }}>
            <Tooltip title="Ver métricas">
              <Button size="small" icon={<BarChartOutlined />} onClick={() => onMetricas(c)} aria-label="Ver métricas" />
            </Tooltip>
            <Tooltip title="Abrir en Brevo">
              <Button
                size="small"
                icon={<ExportOutlined />}
                href={urlCampanaBrevo(b.id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Abrir en Brevo"
              />
            </Tooltip>
          </Space>
        )}
      </div>
      <div style={{ marginTop: 4 }}>
        {b ? (
          <Text type="secondary">
            Campaña {b.id} ·{' '}
            <Text strong style={{ color: fct.marca['verde-base'] }}>{b.destinatarios ?? '—'} en Brevo</Text>
            {' '}· {calculados} en Gescom{' '}
            <Tooltip title={NOTA_DIFERENCIA}><InfoCircleOutlined /></Tooltip>
          </Text>
        ) : (
          <Text type="secondary">
            {consultado ? 'Aún no está en Brevo' : 'Sin consultar Brevo'} · {calculados} en Gescom
          </Text>
        )}
      </div>
    </div>
  );
};

/** Panel lateral con las métricas de una campaña. */
const MetricasCampana: React.FC<{ c: CampanaTipoSemana | null; calculados: number; onClose: () => void }> = (
  { c, calculados, onClose },
) => {
  const b = c?.brevo;
  const enviada = b?.estado === 'sent';
  return (
    <Drawer
      open={!!b}
      onClose={onClose}
      width={520}
      title={b ? `Métricas · ${b.nombre}` : ''}
      extra={b && <AbrirEnBrevo id={b.id} size="middle" />}
    >
      {b && (
        <>
          <Descriptions column={1} size="small" bordered style={{ marginBottom: 20 }}>
            <Descriptions.Item label="Campaña">{b.id}</Descriptions.Item>
            <Descriptions.Item label="Estado">{estadoDe(b)?.texto ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Creada">{fechaHora(b.fechaCreacion)}</Descriptions.Item>
            <Descriptions.Item label="Enviada">{fechaHora(b.fechaEnvio)}</Descriptions.Item>
            <Descriptions.Item label="En Gescom">{calculados}</Descriptions.Item>
            <Descriptions.Item label={enviada ? 'Enviados por Brevo' : 'En las listas de Brevo'}>
              {b.destinatarios ?? '—'}
            </Descriptions.Item>
          </Descriptions>

          {enviada ? (
            <Row gutter={[16, 20]}>
              <Col span={12}><Statistic title="Enviados" value={b.destinatarios ?? '—'} /></Col>
              <Col span={12}>
                <Statistic title="Entregados" value={conPorcentaje(b.entregados, b.destinatarios)} />
              </Col>
              <Col span={12}>
                <Statistic title="Aperturas únicas" value={conPorcentaje(b.aperturas, b.entregados)} />
              </Col>
              <Col span={12}>
                <Statistic title="Clics únicos" value={conPorcentaje(b.clics, b.entregados)} />
              </Col>
              <Col span={12}><Statistic title="Bajas" value={conPorcentaje(b.bajas, b.entregados)} /></Col>
              <Col span={12}><Statistic title="Rebotes" value={conPorcentaje(b.rebotes, b.destinatarios)} /></Col>
            </Row>
          ) : (
            <Alert type="info" showIcon message="La campaña aún no se ha enviado: no hay métricas todavía." />
          )}

          <Alert
            type="info"
            showIcon
            style={{ marginTop: 20 }}
            message={NOTA_DIFERENCIA}
            description="Aperturas, clics y bajas son sobre los entregados. Las aperturas pueden quedarse cortas: algunos clientes de correo bloquean el aviso de apertura. El detalle por contacto está en Brevo («Abrir en Brevo»)."
          />
        </>
      )}
    </Drawer>
  );
};

/**
 * Campañas de Brevo de la semana elegida (ROC/OC/SI) y sus métricas. Solo lectura: lo que se crea
 * en Brevo lo hace el flujo n8n (o, hasta el cambio, el sistema anterior).
 */
const CampanasBrevo: React.FC<Props> = ({ datos, loading, calculados }) => {
  const [abierta, setAbierta] = useState<CampanaTipoSemana | null>(null);

  if (loading && !datos) return <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 16 }} />;
  if (!datos) return null;

  const consultado = datos.brevoConfigurado && !datos.errorBrevo;
  return (
    <div style={{ marginBottom: 16 }}>
      {!datos.brevoConfigurado && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Brevo no está conectado todavía: se muestran los nombres que tendrán las campañas, sin su estado."
        />
      )}
      {datos.errorBrevo && (
        <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={`No se pudo consultar Brevo: ${datos.errorBrevo}`} />
      )}
      {/* Al cambiar de semana, las tarjetas de la semana anterior se atenúan hasta que responde Brevo. */}
      <Spin spinning={loading} tip="Consultando Brevo…">
        <Row gutter={[12, 12]}>
          {datos.campanas.map((c) => (
            <Col xs={24} md={8} key={c.tipo}>
              <TarjetaCampana c={c} calculados={calculados[c.tipo] ?? 0} consultado={consultado} onMetricas={setAbierta} />
            </Col>
          ))}
        </Row>
      </Spin>
      <MetricasCampana
        c={abierta}
        calculados={abierta ? calculados[abierta.tipo] ?? 0 : 0}
        onClose={() => setAbierta(null)}
      />
    </div>
  );
};

export default CampanasBrevo;
